import React, { Component, ErrorInfo, ReactNode } from 'react';
import { WarningCircle, ArrowClockwise, House } from '@phosphor-icons/react';
import { motion } from 'framer-motion';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  private handleGoHome = () => {
    this.setState({ hasError: false, error: null });
    window.location.hash = '/';
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-background relative overflow-hidden">
          {/* Animated background blobs */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <motion.div
              className="absolute w-[300px] h-[300px] rounded-full blur-3xl opacity-30"
              style={{ 
                background: 'radial-gradient(circle, hsl(var(--destructive) / 0.4) 0%, transparent 70%)',
                top: '20%',
                left: '10%',
              }}
              animate={{
                x: [0, 30, 0],
                y: [0, -20, 0],
                scale: [1, 1.1, 1],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            <motion.div
              className="absolute w-[250px] h-[250px] rounded-full blur-3xl opacity-20"
              style={{ 
                background: 'radial-gradient(circle, hsl(var(--primary) / 0.3) 0%, transparent 70%)',
                bottom: '30%',
                right: '15%',
              }}
              animate={{
                x: [0, -25, 0],
                y: [0, 25, 0],
                scale: [1, 1.15, 1],
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          </div>

          {/* Glass card */}
          <motion.div 
            className="relative z-10 text-center max-w-sm w-full"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            <div className="glass-card p-8 rounded-3xl">
              {/* Error icon */}
              <motion.div 
                className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, hsl(var(--destructive) / 0.15) 0%, hsl(var(--destructive) / 0.05) 100%)',
                  boxShadow: '0 8px 32px hsl(var(--destructive) / 0.2), inset 0 1px 0 hsl(var(--background) / 0.5)',
                }}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 15 }}
              >
                <WarningCircle className="w-10 h-10 text-destructive" weight="fill" />
              </motion.div>

              <motion.h2 
                className="text-xl font-semibold text-foreground mb-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                Что-то пошло не так
              </motion.h2>
              
              <motion.p 
                className="text-muted-foreground mb-8 text-sm leading-relaxed"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                Произошла ошибка при загрузке. Попробуйте обновить страницу или вернуться на главную.
              </motion.p>

              {/* Action buttons */}
              <motion.div 
                className="flex flex-col gap-3"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <motion.button
                  onClick={this.handleRetry}
                  className="w-full py-3.5 px-6 rounded-2xl font-medium flex items-center justify-center gap-2.5 transition-all duration-200"
                  style={{
                    background: 'linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary) / 0.8) 100%)',
                    color: 'hsl(var(--primary-foreground))',
                    boxShadow: '0 4px 20px hsl(var(--primary) / 0.3), inset 0 1px 0 hsl(var(--background) / 0.2)',
                  }}
                  whileTap={{ scale: 0.97 }}
                >
                  <ArrowClockwise className="w-5 h-5" weight="bold" />
                  Обновить страницу
                </motion.button>

                <motion.button
                  onClick={this.handleGoHome}
                  className="w-full py-3.5 px-6 rounded-2xl font-medium flex items-center justify-center gap-2.5 transition-all duration-200"
                  style={{
                    background: 'hsl(var(--muted) / 0.5)',
                    backdropFilter: 'blur(12px)',
                    color: 'hsl(var(--foreground))',
                    border: '1px solid hsl(var(--border) / 0.5)',
                  }}
                  whileTap={{ scale: 0.97 }}
                >
                  <House className="w-5 h-5" weight="bold" />
                  На главную
                </motion.button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      );
    }

    return this.props.children;
  }
}
